// Which conversations hold messages you haven't seen.
//
// ── WHERE "READ" LIVES ────────────────────────────────────────────────────
// One row per (employee, thread) in hq_chat_reads: the moment you last had
// that conversation open. A message is unread when it arrived after your mark
// — or, for a thread you've never opened since this shipped, when it arrived
// in the last three days: without that floor, day one would bold every
// conversation back to June, which tells nobody anything.
//
// A thread is identified by a key, not a table reference, because two shapes
// share one inbox: channels ("channel:<channel id>") and DMs ("dm:<the other
// person's employee id>") — a DM thread is a PERSON, whichever direction the
// messages flow.

export interface ChatMessageFacts {
  sender_id: string;
  recipient_id: string | null;
  channel_id: string | null;
  is_direct_message: boolean;
}

export const threadKey = (kind: "channel" | "dm", id: string): string => `${kind}:${id}`;

/**
 * The thread an incoming message makes unread for `myId` — or null when it
 * makes nothing unread: my own messages (I have read what I wrote, wherever
 * I wrote it), and DMs between two other people.
 */
export function messageThreadKey(m: ChatMessageFacts, myId: string): string | null {
  if (m.sender_id === myId) return null;
  if (m.is_direct_message) {
    return m.recipient_id === myId ? threadKey("dm", m.sender_id) : null;
  }
  return m.channel_id ? threadKey("channel", m.channel_id) : null;
}

/**
 * Fold an incoming message into the unread counts. The thread on screen never
 * accumulates unread — being open IS being read. Returns the same object when
 * nothing changes, so React sees no update to render.
 */
export function bumpUnread(
  unread: Record<string, number>,
  m: ChatMessageFacts,
  myId: string,
  activeKey: string | null,
): Record<string, number> {
  const key = messageThreadKey(m, myId);
  if (!key || key === activeKey) return unread;
  return { ...unread, [key]: (unread[key] ?? 0) + 1 };
}

/** Opening a thread clears it. Same-object when there was nothing to clear. */
export function clearUnread(unread: Record<string, number>, key: string): Record<string, number> {
  if (!unread[key]) return unread;
  const next = { ...unread };
  delete next[key];
  return next;
}

/**
 * Sidebar order: whoever is waiting on you comes to you.
 *
 * Unread conversations float to the top, the one that spoke most recently
 * first — a bold name at the bottom of a seventy-person list is barely better
 * than no indicator at all. Everything read keeps the list's own order
 * (alphabetical), and the sort is STABLE on purpose: names must not shuffle
 * when nothing about them changed.
 */
export function sortUnreadFirst<T>(
  items: T[],
  keyOf: (item: T) => string,
  unread: Record<string, number>,
  latest: Record<string, number>,
): T[] {
  const lit = items.filter(i => (unread[keyOf(i)] ?? 0) > 0);
  if (lit.length === 0) return items;   // same array: React sees no change
  const rest = items.filter(i => (unread[keyOf(i)] ?? 0) === 0);
  lit.sort((a, b) => (latest[keyOf(b)] ?? 0) - (latest[keyOf(a)] ?? 0));
  return [...lit, ...rest];
}
